import _ from 'lodash'
import RequestError from './RequestError'
import api from '@/api'

/**
 * Workspaces partition one account's resources (streams, VOD, playlists,
 * destinations, showcases, transcoders, team) while sharing its plan.
 *
 * The API calls the same resource a "workspace" — every path below is
 * `/workspaces/...` — so grep for either word when tracing a request.
 */

/**
 * @typedef RequestConfig
 * @prop {string} path
 * @prop {string} method
 * @prop {Object} [params]
 * @prop {any} [data]
 */

export default {
  getProjects,
  getActiveProject,
  getAllowance,
  createProject,
  updateProject,
  deleteProject,
  selectProject,
  getProjectUsage,
  getResourceUsage,
}

/**
 * The switcher's single source of truth: accessible workspaces, which one is
 * active, the plan allowance, and the accent palette.
 */
function getProjects() {
  return makeRequest('/workspaces')
}

function getActiveProject() {
  return makeRequest('/workspaces/active')
}

function getAllowance() {
  return makeRequest('/workspaces/allowance')
}

/**
 * @param {string} name
 * @param {string} color
 */
function createProject(name, color) {
  return makeRequest({
    path: '/workspaces',
    method: 'post',
    data: { name, color }
  })
}

/**
 * @param {string} projectId
 * @param {{ name?: string, color?: string }} updates
 */
function updateProject(projectId, updates) {
  return makeRequest({
    path: `/workspaces/${projectId}`,
    method: 'put',
    data: updates
  })
}

/**
 * @param {string} projectId
 */
function deleteProject(projectId) {
  return makeRequest({
    path: `/workspaces/${projectId}`,
    method: 'delete'
  })
}

/**
 * Makes `projectId` the active workspace for the session. Every other endpoint
 * (`/streams`, `/my-teams`, …) answers in the context of the active workspace,
 * which is why switching is followed by a full reload rather than by
 * re-fetching a dozen stores one by one.
 *
 * @param {string} projectId
 */
function selectProject(projectId) {
  return makeRequest({
    path: `/workspaces/select/${projectId}`,
    method: 'post'
  })
}

/**
 * Per-project attribution of the account's shared pool, for the Billing page.
 */
function getProjectUsage() {
  return makeRequest('/workspaces/usage')
}

/**
 * How much of each resource sits in the active workspace, and how much the
 * account holds altogether — the two numbers a solution page needs to say
 * "1 here, 2 of 4 on the plan". Lists are workspace-scoped but allowances are
 * not, so the list length alone cannot answer the second half.
 */
function getResourceUsage() {
  return makeRequest('/workspaces/resource-usage')
}

/**
 * @param {RequestConfig|string} reqConfig
 */
async function makeRequest(reqConfig) {
  if (typeof reqConfig === 'string') {
    reqConfig = { path: reqConfig }
  }

  reqConfig.url = reqConfig.path

  let res
  try {
    res = await api.request(reqConfig)
  } catch (err) {
    let edata = _.get(err, 'response.data')
    throw new RequestError(edata)
  }

  return res.data
}
